import { FEED_KIND, PRIVATE_MIRROR, feedPrefix } from './feedContext';
import { lenta5ApiPath } from './lenta5';
/**
 * Параметры, вшитые в бандл на сборке.
 *
 * Появились ради второго контура. Интерфейс, математика и фильтрация у v1 и v2
 * общие — различается только то, откуда берутся вилки и по какому адресу стоит
 * API. Держать ради этого вторую копию фронта нельзя: правило паритета расчётов
 * (`AGENTS.md`, раздел «Числа») требует, чтобы бэкенд и фронт считали одинаково
 * до второго знака, а две копии кода расходятся в первый же день.
 *
 * Поэтому бандл один, а контур выбирается переменной сборки:
 *
 *   v1  VITE_API_BASE=""      → запросы идут в корень домена
 *   lab VITE_API_BASE=""      → та же лента, страница под /lab (BASE_URL=/lab/)
 *   v2  VITE_API_BASE="/v2"   → запросы идут в /v2, Caddy срезает префикс
 *
 * Пустая база — поведение v1 один в один, поэтому существующая сборка не меняет
 * ни одного своего адреса.
 */

/** Убирает хвостовые слэши: `/v2/` и `/v2` обязаны давать один и тот же адрес. */
const normalize = (value: unknown): string => String(value ?? '').trim().replace(/\/+$/, '');

/** Префикс всех обращений к API. Пусто для v1. */
export const API_BASE = normalize(import.meta.env.VITE_API_BASE);

/**
 * Подпись контура в шапке. Пустая у v1 — иначе пришлось бы трогать боевой
 * заголовок ради второго стенда. У v2 подпись обязательна: две одинаковые с
 * виду вкладки с разными лентами — прямой способ поставить не по той цене.
 */
export const APP_LABEL = String(import.meta.env.VITE_APP_LABEL ?? '').trim();

/**
 * Второй контур: два чужих зеркала в одной ленте. Сборка v2 ставит и базу, и
 * метку; любой из двух признаков достаточен, чтобы не принять `ch_a`/`ch_v`
 * за конторы (18.09, пустое сито и «2 площадки в эфире»).
 */
export const MIRROR_FEED = API_BASE === '/v2' || APP_LABEL === 'V2' ||
  (typeof location !== 'undefined' && (
    location.pathname.startsWith('/tests') || location.pathname.startsWith('/feed3')
  ));

/**
 * Сборка /tests открыта без VITE_API_BASE и знает только свой /tests-api.
 * Лайв, русские и переливы с этой вкладки идут в /v2: корневой /v1 — другой шлюз.
 * Запоминаем адрес загрузки: после смены ленты путь уже /app, а сборка та же.
 */
const OPENED_ON_TESTS =
  typeof location !== 'undefined' &&
  (location.pathname === '/tests' || location.pathname.startsWith('/tests/'));

function scopeBase(): string {
  if (API_BASE !== '') return API_BASE;
  if (OPENED_ON_TESTS && FEED_KIND !== 'tests' && FEED_KIND !== 'feed3') return '/v2';
  return '';
}

function onDry(): boolean {
  return typeof location !== 'undefined' && /^\/dry(?:\/|$)/.test(location.pathname);
}

/** Адрес ручки API этого контура. */
export function apiUrl(path: string, forkId?: string): string {
  if (PRIVATE_MIRROR && /^\/v1\/(?:meta|signals|fork|why|hours|profiles|attempt|sighting|experiments|cupis|reports|reviews|calls)(?:[/?]|$)/.test(path)) {
    return `/cupis-api${path}`;
  }
  // /dry owns feed4 even though prepared fork ids share the exp: namespace.
  // Keep authentication and trading endpoints on their existing gateway.
  if (onDry() && /^\/v1\/(?:meta|signals|fork|why|hours|profiles|attempt|sighting|experiments)(?:[/?]|$)/.test(path)) {
    return `/dry-api${path}`;
  }
  const v5 = lenta5ApiPath(path);
  if (v5 !== null) return v5;
  const scoped = /^\/v1\/(?:meta|signals|fork|why|hours|profiles|attempt|sighting|experiments)(?:[/?]|$)/.test(path);
  const prefix = feedPrefix();
  const readyForkPath = /^\/v1\/fork\/exp(?::|%3a)(?:live|prematch)(?::|%3a)/i.test(path);
  const readyNote = /^\/v1\/sighting(?:[/?]|$)/.test(path)
    && /^exp:(?:live|prematch):/.test(forkId ?? '');
  const target = prefix === '' && (readyForkPath || readyNote) ? '/tests-api' : prefix;
  return `${scopeBase()}${scoped ? target : ''}${path}`;
}

/**
 * Адрес сокета ленты. Схема берётся у страницы, а не задаётся константой: на
 * локальной машине это `ws`, на проде `wss`, и захардкоженная схема ломает
 * ровно один из двух случаев.
 */
export function feedSocketUrl(location: { protocol: string; host: string }): string {
  const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
  if (PRIVATE_MIRROR) return `${scheme}://${location.host}/cupis-api/ws`;
  if (onDry()) return `${scheme}://${location.host}/dry-api/ws`;
  return `${scheme}://${location.host}${scopeBase()}${feedPrefix()}/ws`;
}
